/*
 * Fonts of the terminal: popular open monospace fonts, self-hosted from
 * @fontsource packages (our origin only: the CSP allows no font CDN) and
 * loaded only when a font is chosen or shown in the settings list. Licences
 * are listed in docs/terminal.md ("Fonts"). "system" uses the device's own
 * monospace font and loads nothing.
 */

export const SYSTEM_FONT = "system";

/** After the chosen font: what the device has, so missing glyphs still draw. */
const FALLBACK = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace';

export interface TerminalFont {
  id: string;
  /** The family name in CSS (and shown in the list, in that font). */
  family: string;
  /** Has Cyrillic letters (otherwise they come from the fallback). */
  cyrillic: boolean;
  /** Loads the regular face; `bold` also the bold one (the font may have none). */
  load: (bold: boolean) => Promise<unknown>;
}

const font = (
  id: string,
  family: string,
  cyrillic: boolean,
  regular: () => Promise<unknown>,
  bold?: () => Promise<unknown>,
): TerminalFont => ({
  id,
  family,
  cyrillic,
  load: (withBold) => Promise.all([regular(), withBold && bold ? bold() : null]),
});

// Each import names its file literally: the bundler makes one lazy chunk per face.
export const FONTS: readonly TerminalFont[] = [
  font(
    "jetbrains-mono",
    "JetBrains Mono",
    true,
    () => import("@fontsource/jetbrains-mono/400.css"),
    () => import("@fontsource/jetbrains-mono/700.css"),
  ),
  font(
    "fira-code",
    "Fira Code",
    true,
    () => import("@fontsource/fira-code/400.css"),
    () => import("@fontsource/fira-code/700.css"),
  ),
  font(
    "fira-mono",
    "Fira Mono",
    true,
    () => import("@fontsource/fira-mono/400.css"),
    () => import("@fontsource/fira-mono/700.css"),
  ),
  font(
    "source-code-pro",
    "Source Code Pro",
    true,
    () => import("@fontsource/source-code-pro/400.css"),
    () => import("@fontsource/source-code-pro/700.css"),
  ),
  font(
    "ibm-plex-mono",
    "IBM Plex Mono",
    true,
    () => import("@fontsource/ibm-plex-mono/400.css"),
    () => import("@fontsource/ibm-plex-mono/700.css"),
  ),
  font(
    "cascadia-code",
    "Cascadia Code",
    true,
    () => import("@fontsource/cascadia-code/400.css"),
    () => import("@fontsource/cascadia-code/700.css"),
  ),
  font(
    "roboto-mono",
    "Roboto Mono",
    true,
    () => import("@fontsource/roboto-mono/400.css"),
    () => import("@fontsource/roboto-mono/700.css"),
  ),
  font(
    "ubuntu-mono",
    "Ubuntu Mono",
    true,
    () => import("@fontsource/ubuntu-mono/400.css"),
    () => import("@fontsource/ubuntu-mono/700.css"),
  ),
  font(
    "noto-sans-mono",
    "Noto Sans Mono",
    true,
    () => import("@fontsource/noto-sans-mono/400.css"),
    () => import("@fontsource/noto-sans-mono/700.css"),
  ),
  font("pt-mono", "PT Mono", true, () => import("@fontsource/pt-mono/400.css")),
  font(
    "inconsolata",
    "Inconsolata",
    false,
    () => import("@fontsource/inconsolata/400.css"),
    () => import("@fontsource/inconsolata/700.css"),
  ),
  font(
    "anonymous-pro",
    "Anonymous Pro",
    true,
    () => import("@fontsource/anonymous-pro/400.css"),
    () => import("@fontsource/anonymous-pro/700.css"),
  ),
];

/** The default for new browsers: drawn the same everywhere, with Cyrillic. */
export const DEFAULT_FONT = "jetbrains-mono";

export const fontById = (id: string): TerminalFont | undefined => FONTS.find((f) => f.id === id);

/** The CSS `font-family` of a font id: the font, then the device's monospace. */
export function fontStack(id: string): string {
  const chosen = fontById(id);
  return chosen ? `"${chosen.family}", ${FALLBACK}` : FALLBACK;
}

const loaded = new Map<string, Promise<void>>();

/**
 * Loads a font's faces and waits until the browser can draw with it (xterm
 * measures the cell size once, so it must not measure a fallback). Never
 * rejects: a font that fails to load leaves the fallback in place.
 */
export function loadFont(id: string, bold = true): Promise<void> {
  const chosen = fontById(id);
  if (!chosen) return Promise.resolve();
  const key = `${id}:${bold ? "bold" : "regular"}`;
  let promise = loaded.get(key);
  if (!promise) {
    promise = chosen
      .load(bold)
      .then(() =>
        Promise.all([
          document.fonts.load(`400 16px "${chosen.family}"`),
          bold ? document.fonts.load(`700 16px "${chosen.family}"`) : null,
        ]),
      )
      .then(
        () => undefined,
        () => {
          loaded.delete(key);
        },
      );
    loaded.set(key, promise);
  }
  return promise;
}
